/**
 * Fireworks Effect Component
 * Occasional firework bursts for celebratory themes
 */

import { useEffect, useRef } from 'react'

interface FireworksProps {
  intensity?: 'low' | 'medium' | 'high'
}

const COLORS = ['#FF6B6B', '#4ECDC4', '#FFD93D', '#6BCB77', '#4D96FF', '#FF8C42']

interface Particle {
  x: number
  y: number
  vx: number
  vy: number
  color: string
  life: number
  maxLife: number
  decay: number
}

interface Burst {
  x: number
  y: number
  particles: Particle[]
}

export function Fireworks({ intensity = 'medium' }: FireworksProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const animationFrameRef = useRef<number>()
  const burstsRef = useRef<Burst[]>([])
  const nextBurstRef = useRef(0)

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const resizeCanvas = () => {
      canvas.width = window.innerWidth
      canvas.height = window.innerHeight
    }
    resizeCanvas()
    window.addEventListener('resize', resizeCanvas)

    const intensitySettings = {
      low: { interval: 4000, particleCount: 40 },
      medium: { interval: 2500, particleCount: 60 },
      high: { interval: 1500, particleCount: 90 },
    }
    const settings = intensitySettings[intensity]

    const createBurst = () => {
      const x = Math.random() * canvas.width * 0.6 + canvas.width * 0.2
      const y = Math.random() * canvas.height * 0.4 + canvas.height * 0.2
      const color = COLORS[Math.floor(Math.random() * COLORS.length)]
      const particles: Particle[] = []
      for (let i = 0; i < settings.particleCount; i++) {
        const angle = (Math.PI * 2 * i) / settings.particleCount + Math.random() * 0.5
        const speed = Math.random() * 4 + 3
        particles.push({
          x,
          y,
          vx: Math.cos(angle) * speed,
          vy: Math.sin(angle) * speed,
          color,
          life: 1,
          maxLife: 1,
          decay: Math.random() * 0.02 + 0.015,
        })
      }
      burstsRef.current.push({ x, y, particles })
    }

    let lastTime = performance.now()
    const animate = (now: number) => {
      const dt = Math.min((now - lastTime) / 1000, 0.1)
      lastTime = now

      ctx.clearRect(0, 0, canvas.width, canvas.height)

      if (now >= nextBurstRef.current) {
        createBurst()
        nextBurstRef.current = now + settings.interval + Math.random() * 1000
      }

      burstsRef.current = burstsRef.current.filter((burst) => {
        burst.particles.forEach((p) => {
          p.x += p.vx * dt * 60
          p.y += p.vy * dt * 60
          p.vy += 80 * dt
          p.life -= p.decay
        })
        burst.particles = burst.particles.filter((p) => p.life > 0)
        return burst.particles.length > 0
      })

      burstsRef.current.forEach((burst) => {
        burst.particles.forEach((p) => {
          ctx.beginPath()
          ctx.arc(p.x, p.y, 2, 0, Math.PI * 2)
          ctx.fillStyle = p.color
          ctx.globalAlpha = p.life
          ctx.fill()
        })
      })
      ctx.globalAlpha = 1

      animationFrameRef.current = requestAnimationFrame(animate)
    }

    nextBurstRef.current = performance.now() + 800
    animationFrameRef.current = requestAnimationFrame(animate)

    return () => {
      window.removeEventListener('resize', resizeCanvas)
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current)
      }
    }
  }, [intensity])

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: 0,
      }}
      aria-hidden="true"
    />
  )
}
